import React, {useEffect, useState, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, Link} from 'react-router-dom';
import PageBase from "../../../components/PageBase";
import Field from "../../../components/FieldGroup/Field";
import bullet from '../../../assets/title-bullet.png';
import {formatDate} from "../../../utils/general";
import * as ROUTES from "../../../utils/routes";
import * as opportunityActions from "../../../redux/Opportunity/actions";
import FieldGroup from "../../../components/FieldGroup/index";
import ObjectSummaryRow from "../../../components/ObjectSummaryRow";
import Button from "../../../components/Button";
import PopupWin from "../../../components/PopupWin";
import PopupButton from "../../../components/PopupButton";
import EditOpportunity from "../EditOpportunity";

import './ViewOpportunity.scss';

const ViewOpportunity = props => {
	const [loading, setLoading] = useState(true);
	const [editOpportunity, setEditOpportunity] = useState(null);
	const editOpportunityRef = useRef(null);
	const dispatch = useDispatch();
	const params = useParams();

	let opportunity = useSelector(state => state.opportunity.opportunities?.find(op => op?.Id === params.id));

	const editOpportunityFooter = useMemo(() => (
		<PopupButton
			variant="primary"
			onClick={() => editOpportunityRef.current?.save()}
		>
			Save
		</PopupButton>
	), []);

	useEffect(() => {
		const load = async () => {
			if(opportunity == null && params.id) {
				dispatch(opportunityActions.loadOpportunityById(params.id))
			}
		};
		load();
	}, [opportunity, params.id]);

	if(opportunity == null) {
		return <div/>;
	}

	const onOpportunitySaved = async () => {
		setEditOpportunity(null);
		setLoading(true);
		await dispatch(opportunityActions.loadOpportunityById(params.id));
		setLoading(false);
	};

	const summary = [
		{ title: 'End Customer', value: opportunity.Name, blue: true},
		{ title: 'Amount', value: opportunity.Amount},
		{ title: 'Stage', value: opportunity.StageName},
		{ title: 'Priority', value: opportunity.Priority__c},
		{ title: 'Opportunity Heat', value: opportunity.Opportunity_Heat__c},
		{ title: 'Next Step POC', value: opportunity.Next_Step_POC__r?.Name},
	];

	return (
		<PageBase title="Opportunity"
		          removeMargins
		          bodyBg="#fff"
		          headerLinkText="All Opportunities"
		          headerLinkTarget={ROUTES.OPPORTUNITIES}
		>

			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={editOpportunity != null}
				onClose={() => setEditOpportunity(null)}
				title="Opportunity"
				titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
				contentStyle={{width: '100%', height: '90%', padding: 0}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
				footerStyle={{borderTop: '1px solid #D3E1ED'}}
				footer={editOpportunityFooter}>
				{ editOpportunity && <EditOpportunity ref={editOpportunityRef} editMode={true} opportunity={editOpportunity} onClose={onOpportunitySaved} /> }
			</PopupWin>

			<div className="view-opportunity-title">
				<img src={bullet} style={{width: '24px', marginRight: '20px'}} />
				<span style={{whiteSpace: 'nowrap'}}>{opportunity.Name}</span>

				<div className="opps-header-buttons">
					<Button onClick={() => setEditOpportunity(opportunity)} type="blue" center>Edit Opportunity</Button>
				</div>

			</div>

			<ObjectSummaryRow data={summary} />

			<div style={{height: '100%', overflowY: 'auto', padding: '0 20px'}}>
				<FieldGroup title="Details" expandable expanded>
					<Field title="Opportunity Owner" highlight>{opportunity.Owner_First_Name__c}</Field>
					<Field title="Opportunity Name">{opportunity.Name}</Field>

					<Field title="Account Name">{opportunity.Account.Name}</Field>
					<Field title="End Customer">{opportunity.End_Customer__r?.Name}</Field>

					<Field title="Opportunity Type">{opportunity.Activity_Type__c}</Field>
					<Field title="Cloud POC">{opportunity.Cloud_POC__r?.Name}</Field>

					<Field title="Leading Cloud">{opportunity.Leading_Cloud__c}</Field>
					<Field title="Customer Status">{opportunity.Customer_Status__c}</Field>

					<Field title="Stage">{opportunity.StageName}</Field>
					<Field title="Probability (%)">{opportunity.Probability}</Field>

					<Field title="Priority">{opportunity.Priority__c}</Field>
					<Field title="Opportunity Heat">{opportunity.Opportunity_Heat__c}</Field>

					<Field title="ISV Opportunity Owner">{opportunity.ISV_Opportunity_Owner__c}</Field>
					<Field title="ISV Opportunity Stage">{opportunity.ISV_Opportunity_Stage__c}</Field>

					<Field title="Close Date">{opportunity.CloseDate}</Field>
					<Field title="Is for marketplace">{opportunity.Is_for_Marketplace__c}</Field>

					<Field title="Propensity to Buy">{opportunity.Propensity_to_Buy__c}</Field>
					<Field title="ACE/Partner Center Status">{opportunity.ACE_Partner_Center_Status__c}</Field>

					<Field title="Is on ACE/Partner Center?">{opportunity.Is_on_ACE_PSC__c}</Field>
					<Field title="Use Case Details">{opportunity.Use_Case_Details__c}</Field>

					<Field title="Unique Identifier">{opportunity.Unique_Identifier__c}</Field>
					<Field title="Description">{opportunity.Description}</Field>


				</FieldGroup>

				<FieldGroup title="Activity" expandable>
					<Field title="Next Step">{opportunity.NextStep}</Field>
					<Field title="Next Step Date">{opportunity.Next_Step_Date__c}</Field>

					<Field title="Next Step POC">{opportunity.Next_Step_POC__r?.Name}</Field>
					<Field title="Next Step Owner">{opportunity.Next_Step_Owner__r?.Name}</Field>

					<Field title="Account Team First Call">{formatDate(opportunity.Account_Team_First_Call__c)}</Field>
					<Field title="Customer First Meeting">{formatDate(opportunity.Customer_First_Meeting__c)}</Field>

					<Field title="On Hold?">{opportunity.On_Hold__c}</Field>
					<Field title="Campaign">{opportunity.Campaign__c}</Field>
				</FieldGroup>

				<FieldGroup title="Commercials" expandable>
					<Field title="Amount">{opportunity.Amount}</Field>
					<Field title="Amount, CONNACT">{opportunity.Amount_CONNACT__c}</Field>

					<Field title="Deal Term (M)">{opportunity.Deal_Term_M__c}</Field>
					<Field title="MRR">{opportunity.MRR__c}</Field>

					<Field title="Payment Terms">{opportunity.Payment_Terms__c}</Field>
					<Field title="Contract Effective Date">{opportunity.Contract_Effective_Date__c}</Field>

					<Field title="Renewal date">{opportunity.Renewal_date__c}</Field>
				</FieldGroup>

				<FieldGroup title="Lost Opportunities" expandable>
					<Field title="Lost Reason">{opportunity.Lost_Reason__c}</Field>
				</FieldGroup>

				<FieldGroup title="System Information" expandable>
					<Field title="Created By">{opportunity.CreatedBy.Name}</Field>
					<Field title="Last Modified By">{opportunity.LastModifiedBy.Name}</Field>

					<Field title="CONNACT AM Email">{opportunity.CONNACT_AM_Email__c}</Field>
				</FieldGroup>
			</div>
		</PageBase>
	)
};

export default ViewOpportunity;
